import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { IPrintJob } from '@/interfaces/printing.interfaces';
import {
  applyPrintJobToFailures,
  IPrintFailureEntry,
  pruneAckedKeys,
  UnifiedPrinterStatus,
} from '@/lib/print/print-health.utils';

/**
 * Print health: success counter, failure entries + acknowledgment state for
 * the app-bar printing widget, and the unified printer-health map.
 *
 * Wiring direction: use-printing / the strategies push INTO this store
 * (addPrintJob → onPrintJob; strategies → setPrinterHealth*). This store
 * never imports use-printing — no import cycle.
 */
export const usePrintHealthStore = defineStore('printHealth', () => {
  /** Successful prints since the chip last faded (widget resets it). */
  const successChipCount = ref(0);

  const failures = ref<IPrintFailureEntry[]>([]);
  /**
   * Keys acknowledged by opening the panel: silences the alarm. A NEW dedupe
   * key re-arms it; retries of a known key don't (the key stays acked).
   * Pruned when entries clear, so a later re-failure re-arms.
   */
  const ackedKeys = ref<string[]>([]);

  /**
   * Unified printer health, pushed by the ACTIVE strategy: bridge keys are
   * the bridge printer ref, native keys are '<type>:<id>'. Legacy strategies
   * never push — no live health (their plugins can't probe).
   */
  const printerHealth = ref<Record<string, UnifiedPrinterStatus>>({});

  const failureCount = computed(() => failures.value.length);

  /** Drives the repeating alarm + the "unseen" badge emphasis. */
  const hasUnackedFailures = computed(() =>
    failures.value.some((entry) => !ackedKeys.value.includes(entry.key))
  );

  const actions = {
    /** THE funnel: every job recorded via printing-store addPrintJob. */
    onPrintJob(job: IPrintJob) {
      if (job.status === 'printed') {
        successChipCount.value += 1;
      }
      failures.value = applyPrintJobToFailures(
        failures.value,
        job,
        job.createdAt.getTime()
      );
      ackedKeys.value = pruneAckedKeys(ackedKeys.value, failures.value);
    },

    /** Chip faded out: the next success starts a fresh burst count. */
    resetSuccessChip() {
      successChipCount.value = 0;
    },

    /** Panel opened: silence the alarm for every currently-known entry. */
    acknowledgeFailures() {
      ackedKeys.value = failures.value.map((entry) => entry.key);
    },

    /** "Marcar como resuelto": drops every entry (human verified). */
    resolveAllFailures() {
      failures.value = [];
      ackedKeys.value = [];
    },

    /** Full replace (bridge printers.list / native poller sweep). */
    setPrinterHealthMap(map: Record<string, UnifiedPrinterStatus>) {
      const current = printerHealth.value;
      const keys = Object.keys(map);
      const unchanged =
        keys.length === Object.keys(current).length &&
        keys.every((key) => current[key] === map[key]);
      if (unchanged) return;
      printerHealth.value = { ...map };
    },

    /** Single-printer update (bridge printer.status / native job outcome). */
    setPrinterHealthEntry(key: string, status: UnifiedPrinterStatus) {
      if (printerHealth.value[key] === status) return;
      printerHealth.value = { ...printerHealth.value, [key]: status };
    },

    /**
     * Branch switch: failure entries and the chip belong to the branch being
     * left. Printer health survives (printers are device-level).
     */
    clear() {
      failures.value = [];
      ackedKeys.value = [];
      successChipCount.value = 0;
    },
  };

  return {
    successChipCount,
    failures,
    ackedKeys,
    printerHealth,
    failureCount,
    hasUnackedFailures,
    ...actions,
  };
});
